import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '@/api/apiCall';
import DonutChart3 from '../components/charts/DonutChart3';
import ErrorState from '@/components/common/ErrorStateComponent';
import LoadingComponent from '@/components/common/LoadingComponent';
import { useQueue } from '@/context/QueueContext';
import HeadingComponent from '../components/shared/heading';
import { processParams } from '../utils/paramsHelper';
import { useNavigate } from 'react-router-dom';

const KEY_MAP = {
    cash:         (yr) => yr?.cashCard?.cash?.percent_value,
    card:         (yr) => yr?.cashCard?.card?.percent_value,
    pos:          (yr) => yr?.payLocation?.POS?.percent_value,
    apm:          (yr) => yr?.payLocation?.APM?.percent_value,
    appWeb:       (yr) => yr?.payLocation?.['APP/WEB']?.percent_value,
    verifier:     (yr) => yr?.payLocation?.VERIFIER?.percent_value,
    cashPos:      (yr) => yr?.cashSourceSplit?.POS?.percent_value,
    cashApm:      (yr) => yr?.cashSourceSplit?.APM?.percent_value,
    cardPos:      (yr) => yr?.cardSourceSplit?.POS?.percent_value,
    cardApm:      (yr) => yr?.cardSourceSplit?.APM?.percent_value,
    cardAppWeb:   (yr) => yr?.cardSourceSplit?.['APP/WEB']?.percent_value,
    cardVerifier: (yr) => yr?.cardSourceSplit?.VERIFIER?.percent_value,
    apmCash:      (yr) => yr?.sourceCashCardSplit?.APM?.cash?.percent_value,
    apmCard:      (yr) => yr?.sourceCashCardSplit?.APM?.card?.percent_value,
    posCash:      (yr) => yr?.sourceCashCardSplit?.POS?.cash?.percent_value,
    posCard:      (yr) => yr?.sourceCashCardSplit?.POS?.card?.percent_value,
    pnd:          (yr) => yr?.pndAppWeb?.PND?.percent_value,
    pndAppWeb:    (yr) => yr?.pndAppWeb?.['APP/WEB']?.percent_value,
    "PND :: CARD":        (yr) => yr?.pndSplit?.['PND :: CARD']?.percent_value,
    "PND :: COINS":        (yr) => yr?.pndSplit?.['PND :: COINS']?.percent_value,
    "PND :: EWALLET":      (yr) => yr?.pndSplit?.['PND :: EWALLET']?.percent_value,
    "PND :: NOTES":        (yr) => yr?.pndSplit?.['PND :: NOTES']?.percent_value,
};

const LABEL_MAP = {
    cash: 'CASH',         card: 'CARD',
    pos: 'POS',           apm: 'APM',
    appWeb: 'APP/WEB',    verifier: 'VERIFIER',
    cashPos: 'POS',       cashApm: 'APM',
    cardPos: 'POS',       cardApm: 'APM',
    cardAppWeb: 'APP/WEB',cardVerifier: 'VERIFIER',
    apmCash: 'CASH',      apmCard: 'CARD',
    posCash: 'CASH',      posCard: 'CARD',
    pnd: 'PND',           pndAppWeb: 'APP/WEB',
    coins: 'COINS',       ewallet: 'EWALLET',
    notes: 'NOTES',
};

const buildChartConfig = (chartCfg) => {
    if (!chartCfg) return { labels: [], colors: [], legend: [] };
    const { keys = [], colors = {} } = chartCfg;
    return {
        labels: keys.map(k => LABEL_MAP[k] || k),
        colors: keys.map(k => colors[k] || '#cccccc'),
        legend: keys.map(k => ({ label: LABEL_MAP[k] || k, color: colors[k] || '#cccccc' })),
    };
};

const buildChartData = (years, contractData, chartCfg) => {
    if (!chartCfg) return [];
    const { keys = [] } = chartCfg;
    return years.map((yr) => {
        const yData = contractData[yr];
        return {
            year: yr,
            values: keys.map(k => {
                const val = KEY_MAP[k]?.(yData) ?? 0;
                return Number(val.toFixed(2));
            }),
        };
    });
};

const DonutChart = ({ year = "", month = "", contract = "", page = null, isRefresh = false }) => {
    const { enqueue } = useQueue();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [firstChartData, setFirstChartData] = useState([]);
    const [secondChartData, setSecondChartData] = useState([]);
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        if (!year || !month || !contract) return;
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;
        fetchData();
    }, [year, month, contract]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            

            const apiParams = processParams(page?.params || {}, { year, month, contract });

            const response = await enqueue(() => apiCall(
                'GET',
                page?.api || '/boardpack/payment-sources',
                apiParams,
                navigate,
                false,
                false,
                isRefresh
            ));

            if (!response?.success) throw new Error(response?.message || 'Failed to fetch data');

            const contractId = apiParams.contract || page?.params?.contract;
            const contractData = response.data?.[contractId] || {};
            const years = Object.keys(contractData).sort();

            setFirstChartData(buildChartData(years, contractData, page?.chart_config?.first_chart));
            setSecondChartData(buildChartData(years, contractData, page?.chart_config?.second_chart));

        } catch (err) {
            console.error('Failed to load data:', err);
            setError(err.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <LoadingComponent />;
    if (error) return <ErrorState error={error} onRetry={fetchData} />;

    const firstCfg = buildChartConfig(page?.chart_config?.first_chart);
    const secondCfg = buildChartConfig(page?.chart_config?.second_chart);

    return (
        <div className="w-full p-5 pb-18 bg-background">
            <div className="w-full mx-auto">
                <div className="flex flex-col gap-0.5 mb-1">
                    {page?.title && <HeadingComponent variant="main">{page.title}</HeadingComponent>}
                    {page?.subtitle && <HeadingComponent variant="sub">{page.subtitle}</HeadingComponent>}
                </div>

                <div className="bg-red-700 dark:bg-red-600 h-6 w-full" />
                <div className="bg-gray-900 dark:bg-gray-950 py-4">

                    {/* First Chart */}
                    <div className="overflow-hidden shadow-lg">
                        {page?.in_chart_content_line_1 && (
                            <h3 className="text-center font-bold text-xs mb-2 text-white">
                                {page.in_chart_content_line_1}
                            </h3>
                        )}
                        <div className="grid grid-cols-3 gap-12 place-items-center mx-4 py-12 bg-white dark:bg-gray-800">
                            {firstChartData.map((data) => (
                                <div key={data.year} className="flex justify-center items-center">
                                    <DonutChart3
                                        data={data.values}
                                        labels={firstCfg.labels}
                                        colors={firstCfg.colors}
                                        year={data.year}
                                        legendItems={firstCfg.legend}
                                        size={280}
                                        cutoutPercentage={65}
                                        labelPosition="outside"
                                        labelStyle="simple"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Second Chart */}
                    <div className="overflow-hidden shadow-lg">
                        {page?.in_chart_content_line_2 && (
                            <h3 className="text-center font-bold my-2 text-xs text-white">
                                {page.in_chart_content_line_2}
                            </h3>
                        )}
                        <div className="grid grid-cols-3 gap-12 place-items-center mx-4 py-12 bg-white dark:bg-gray-800">
                            {secondChartData.map((data) => (
                                <div key={data.year} className="flex justify-center items-center">
                                    <DonutChart3
                                        data={data.values}
                                        labels={secondCfg.labels}
                                        colors={secondCfg.colors}
                                        year={data.year}
                                        legendItems={secondCfg.legend}
                                        size={280}
                                        cutoutPercentage={65}
                                        labelPosition="outside"
                                        labelStyle="detailed"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default DonutChart;